import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Globe } from "lucide-react";
import { STATS, COUNTRIES, CLIENTS_STAT } from "../data/brands";

function Counter({ to, suffix, active }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!active) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [active, to]);

  return (
    <span>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

// Compact clients-only band for the group landing page.
export function ClientsStat() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <section data-testid="stats-section" className="bg-dejure-purple text-white">
      <div ref={ref} className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <div className="grid gap-10 md:grid-cols-[auto_1fr] md:items-center md:gap-20">
          <div data-testid="stat-clients">
            <div className="font-serif text-7xl sm:text-8xl lg:text-[10rem] leading-none tracking-tighter">
              <Counter to={CLIENTS_STAT.value} suffix={CLIENTS_STAT.suffix} active={inView} />
            </div>
          </div>
          <div className="max-w-md">
            <div className="h-px w-16 bg-dejure-silver/60" />
            <p className="mt-6 font-accent text-xs uppercase tracking-[0.25em] text-dejure-silver">
              {CLIENTS_STAT.label}
            </p>
            <p className="mt-5 font-serif text-3xl md:text-4xl leading-tight tracking-tight text-white">
              Trusted by ambitious organisations to protect, grow and elevate what they build.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function StatsSection({ variant = "light" }) {
  const dark = variant === "dark";
  const countersRef = useRef(null);
  const countersInView = useInView(countersRef, { once: true, amount: 0.15 });
  return (
    <section
      data-testid="stats-section"
      className={dark ? "bg-dejure-purple text-white" : "bg-dejure-mist text-dejure-ink"}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr] lg:gap-24 items-center">
          {/* Counters */}
          <div>
            <p className={`font-accent text-xs uppercase tracking-[0.25em] ${dark ? "text-dejure-silver" : "text-dejure-purple"}`}>
              By the numbers
            </p>
            <div ref={countersRef} className="mt-10 grid grid-cols-2 gap-x-8 gap-y-12">
              {STATS.map((s, i) => (
                <motion.div
                  key={s.label}
                  data-testid={`stat-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className={`font-serif text-6xl md:text-7xl leading-none tracking-tighter ${dark ? "text-white" : "text-dejure-purple"}`}>
                    <Counter to={s.value} suffix={s.suffix} active={countersInView} />
                  </div>
                  <div className={`mt-3 h-px w-10 ${dark ? "bg-dejure-silver/60" : "bg-dejure-purple/40"}`} />
                  <p className={`mt-4 font-sans text-sm leading-relaxed ${dark ? "text-white/70" : "text-dejure-ink/60"}`}>
                    {s.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Countries */}
          <div>
            <div className="flex items-center gap-3">
              <Globe size={18} className={dark ? "text-dejure-silver" : "text-dejure-purple"} />
              <p className={`font-accent text-xs uppercase tracking-[0.25em] ${dark ? "text-dejure-silver" : "text-dejure-purple"}`}>
                A global reach
              </p>
            </div>
            <h2 className={`mt-6 font-serif text-4xl sm:text-5xl tracking-tighter ${dark ? "text-white" : "text-dejure-ink"}`}>
              Advising clients across borders, from the heart of the Middle East.
            </h2>
            <div className="mt-10 flex flex-wrap gap-3">
              {COUNTRIES.map((c, i) => (
                <motion.span
                  key={c}
                  data-testid={`country-${i}`}
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className={`font-accent text-xs uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors duration-300 ${
                    dark
                      ? "border-white/25 text-white/80 hover:bg-white hover:text-dejure-purple"
                      : "border-dejure-purple/25 text-dejure-ink/70 hover:bg-dejure-purple hover:text-white"
                  }`}
                >
                  {c}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
